import { ApiMetaData, Metadata } from 'types/api';
import { AppThunk } from 'redux/store';
import { ComisionistActions, ComisionistAction } from 'types/redux/actions/comisionist.action';
import { isSuccessResult } from 'utils/guards';
import {
  Comisionist,
  ComisionistDetail,
  ComisionistType,
  PrimaryStatus,
  SecondaryStatus,
  ComisionistApiResponse,
} from 'types/api/responses';
import { ComisionistFilterParams } from 'types/redux/state/comisionist.state';
import { getCommissionerList, getCommissionerStats, getCommissionerDetail, updateCommissioner } from 'api/karthus.client';

const PAGE_SIZE = 10;

/**
 * Mapea la respuesta del API a la estructura interna de la app
 */
const mapApiResponseToComisionist = (apiData: ComisionistApiResponse, index: number): Comisionist => {
  return {
    id: apiData.clientAccount,
    account: apiData.clientAccount,
    name: apiData.name,
    type: apiData.type,
    dui: apiData.dui,
    nit: apiData.nit,
    nrc: apiData.nrc,
    status: apiData.primaryStatus,
    documentsStatus: apiData.secondaryStatus,
    dateHighAS400: apiData.dateDischarge || '',
    synchronized: !!apiData.lastSync,
    dateSynchronized: apiData.lastSync,
    branchOffice: apiData.branchOfficeId,
    branchOfficeId: apiData.branchOfficeId,
    isLargeContributor: apiData.isLargeContributor,
    identityDoc: apiData.identityDoc,
    nrcDoc: apiData.nrcDoc,
    signedFile: apiData.signedFile,
    verified: apiData.verified,
  };
};

/**
 * Acción para obtener lista de comisionistas con filtros y paginación
 * @param params - Parámetros de paginación
 * @param filter - Filtros a aplicar
 * @param onSuccess - Callback opcional cuando se completa exitosamente
 */
export type FetchComisionistListParams = {
  page?: number;
  size?: number;
  filter?: ComisionistFilterParams;
  onSuccess?: () => void;
};

export const fetchComisionistList = ({
  page = 1,
  size = 10,
  filter = {},
  onSuccess,
}: FetchComisionistListParams): AppThunk<ComisionistAction> => async (dispatch) => {
  dispatch({ type: ComisionistActions.update, isLoading: true });

  try {
    // Construir parámetros del API
    const filterParams: any = {};

    if (filter.name) filterParams.nameContains = filter.name;
    if (filter.account) filterParams.account = filter.account;
    if (filter.type) filterParams.type = filter.type;
    if (filter.primaryStatus) filterParams.primaryStatus = filter.primaryStatus;
    if (filter.secondaryStatus) filterParams.secondaryStatus = filter.secondaryStatus;
    if (filter.OfficeId) filterParams.OfficeId = filter.OfficeId;
    if (filter.ZoneId) filterParams.ZoneId = filter.ZoneId;
    if (filter.updatedAfter) filterParams.updatedAfter = filter.updatedAfter;
    if (filter.sort) filterParams.sort = filter.sort;

    const apiParams: any = {
      page,
      size: size || PAGE_SIZE,
    };

    // Agregar filtros solo si hay parámetros de filtro
    if (Object.keys(filterParams).length > 0) {
      apiParams.filter = filterParams;
    }

    const response = await getCommissionerList(apiParams);

    if (isSuccessResult(response.result)) {
      const data = response.result as any;
      const comisionistas = (data.data || []).map(mapApiResponseToComisionist);
      
      dispatch({
        type: ComisionistActions.fetchList,
        comisionistas,
        meta: data.meta || {
          page,
          pages: 1,
          total: comisionistas.length,
          from: 1,
          to: comisionistas.length,
          size,
        },
      });

      if (onSuccess) {
        onSuccess();
      }
    } else {
      throw new Error('Error al obtener lista de comisionistas');
    }
  } catch (error) {
    dispatch({
      type: ComisionistActions.update,
      isLoading: false,
      error: error as Error,
    });
  }
};

/**
 * Acción para actualizar los filtros del estado
 */
export const setComisionistFilter = (
  filter: ComisionistFilterParams,
): ComisionistAction => ({
  type: ComisionistActions.setFilter,
  filter,
});

/**
 * Acción para crear un nuevo comisionista
 * @param data - Datos del comisionista a crear
 */
export const createComisionist = (data: any): AppThunk<ComisionistAction> => async (
  dispatch,
) => {
  dispatch({ type: ComisionistActions.update, isLoading: true });

  try {
    // TODO: Implementar llamada real al API
    // const response = await apiPost<Comisionist>(...);
    
    const newComisionist: Comisionist = {
      id: data.account || `ACC-${Date.now()}`,
      account: data.account || '',
      name: data.name || '',
      type: data.type || 'Formal',
      dui: data.dui || '',
      nit: data.nit || '',
      nrc: data.nrc || '',
      status: 'New' as PrimaryStatus,
      documentsStatus: 'PendingDocuments' as SecondaryStatus,
      dateHighAS400: new Date().toISOString(),
      synchronized: false,
      dateSynchronized: new Date().toISOString(),
      branchOffice: data.branchOfficeId || '',
      branchOfficeId: data.branchOfficeId || '',
      isLargeContributor: false,
    };

    dispatch({
      type: ComisionistActions.createItem,
      comisionist: newComisionist,
    });
    return Promise.resolve();
  } catch (error) {
    dispatch({
      type: ComisionistActions.update,
      isLoading: false,
      error: error as Error,
    });
    return Promise.reject(error);
  }
};

/**
 * Acción para limpiar el detalle del comisionista
 */
export const clearComisionistDetail = (): ComisionistAction => ({
  type: ComisionistActions.clearDetail,
});

/**
 * Acción para obtener estadísticas de comisionistas
 */
export const fetchComisionistStats =
  (filter: ComisionistFilterParams = {}): AppThunk<ComisionistAction> =>
  async (dispatch) => {
    dispatch({ type: ComisionistActions.update, isLoading: true });

    try {
      const filterParams: Parameters<typeof getCommissionerStats>[0] = {};
      if (filter.name) filterParams.nameContains = filter.name;
      if (filter.account) filterParams.account = filter.account;
      if (filter.type) filterParams.type = filter.type;
      if (filter.primaryStatus) filterParams.primaryStatus = filter.primaryStatus;
      if (filter.secondaryStatus) filterParams.secondaryStatus = filter.secondaryStatus;
      if (filter.OfficeId) filterParams.OfficeId = filter.OfficeId;
      if (filter.ZoneId) filterParams.ZoneId = filter.ZoneId;
      if (filter.updatedAfter) filterParams.updatedAfter = filter.updatedAfter;

      const response = await getCommissionerStats(filterParams);

      if (isSuccessResult(response.result)) {
        const stats = response.result.data;
        dispatch({
          type: ComisionistActions.setStats,
          stats,
        });
      } else {
        throw new Error('Error al obtener estadísticas de comisionistas');
      }
    } catch (error) {
      dispatch({
        type: ComisionistActions.update,
        isLoading: false,
        error: error as Error,
      });
    }
  };

/**
 * Acción para obtener detalles de un comisionista específico
 */
export const fetchComisionistDetail =
  (clientAccount: string): AppThunk<ComisionistAction> =>
  async (dispatch) => {
    dispatch({ type: ComisionistActions.update, isLoading: true });

    try {
      const response = await getCommissionerDetail(clientAccount);

      if (isSuccessResult(response.result)) {
        const apiData = response.result.data as ComisionistApiResponse;
        const comisionist = mapApiResponseToComisionist(apiData, 0);

        // Crear detalle con documentos e historial vacíos (se cargarían en otras llamadas si es necesario)
        const detail: ComisionistDetail = {
          ...comisionist,
          documents: [],
          history: [],
        };

        dispatch({
          type: ComisionistActions.fetchDetail,
          detail,
        });
      } else {
        throw new Error('Error al obtener detalles del comisionista');
      }
    } catch (error) {
      dispatch({
        type: ComisionistActions.update,
        isLoading: false,
        error: error as Error,
      });
    }
  };

/**
 * Acción para actualizar un comisionista existente
 */
export const updateComisionist = (
  clientAccount: string,
  data: any,
  onSuccess?: () => void,
  onError?: (msg: string) => void,
): AppThunk<ComisionistAction> =>
  async (dispatch) => {
    dispatch({ type: ComisionistActions.update, isLoading: true });

    try {
      const body = {
        name: data.name,
        dui: data.dui || '',
        nit: data.nit || '',
        nrc: data.nrc || '',
        clientAccount,
        branchOfficeId: data.branchOfficeId,
        type: data.type,
        isLargeContributor: data.isLargeContributor ?? false,
        primaryStatus: data.status,
        secondaryStatus: data.documentsStatus,
        dateDischarge: data.dateDischarge,
      };

      const response = await updateCommissioner(clientAccount, body);

      if (isSuccessResult(response.result)) {
        dispatch({ type: ComisionistActions.update, isLoading: false });
        if (onSuccess) onSuccess();
      } else {
        const errors = response.result as Error[];
        const msg = errors?.[0]?.message || 'Error al actualizar el comisionista';
        dispatch({ type: ComisionistActions.update, isLoading: false });
        if (onError) onError(msg);
      }
    } catch (error) {
      dispatch({ type: ComisionistActions.update, isLoading: false, error: error as Error });
      if (onError) onError('Error inesperado al actualizar el comisionista');
    }
  };

